/* eslint-disable no-param-reassign */
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { createSearchAPIContract } from 'src/components/search-listing/utils/createSearchAPIContract';
import { SEARCH_API } from 'src/constants/apiEndpoints';
import FetchStatus from 'src/types/FetchStatus';
import {
  DataSet, SearchDataRequestType, SearchRequest, SearchResponse, User,
} from 'src/types/Search';
import { customFetch, Metrics } from 'src/utils';
import { MethodName } from 'src/constants/metrics';
import type { AppState } from '../store';
import { selectMarketplaceId } from './userInfo';

export type UserSearchState = {
  users: Array<User>,
  status: FetchStatus,
  error?: string
};

export const initialState: UserSearchState = {
  users: [],
  status: FetchStatus.idle,
};

let controller: AbortController;
export const fetchUsers = createAsyncThunk(
  'userSearch/fetchUsers',
  async (query: {
    searchUsername: string,
    groupId?: string,
  }, { rejectWithValue, getState }) => {
    if (controller) controller.abort();
    controller = new AbortController();
    return await customFetch(
      SEARCH_API,
      {
        method: 'POST',
        body: JSON.stringify({
          ...createSearchAPIContract({
            dataSetType: DataSet.users,
            searchDataRequestType: SearchDataRequestType.SEARCH_DATA,
            username: query.searchUsername,
            ...(query.groupId && {
              groupId: query.groupId,
            }),
          }),
          marketplaceId: selectMarketplaceId(getState() as AppState),
        } as SearchRequest),
        signal: controller.signal,
      },
      (error: Error) => {
        const { message, name } = error;
        return rejectWithValue({ message, name });
      },
      1,
    ) as SearchResponse;
  },
);

export const userSearch = createSlice({
  name: 'userSearch',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // fetchUsers
      .addCase(fetchUsers.rejected, (state: UserSearchState, action) => {
        const { message, name } = action.payload as { message: string, name: string };
        state.error = message;
        if (name === 'AbortError' && !controller!.signal.aborted) {
          state.status = FetchStatus.loading;
        } else {
          state.status = FetchStatus.failed;
        }
      })
      .addCase(fetchUsers.pending, (state: UserSearchState) => {
        state.users = [];
        state.status = FetchStatus.loading;
      })
      .addCase(fetchUsers.fulfilled, (state: UserSearchState, action) => {
        const searchMetric = new Metrics(`${MethodName.searchRequests}.mention`);
        searchMetric.publishCounterMetric('results', action.payload.users.length);
        state.users = action.payload.users;
        state.status = FetchStatus.complete;
      });
  },
});

export default userSearch.reducer;
